import { useCallback, useState } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { useInstitution } from '@/contexts/InstitutionContext'
import { changePassword } from '@/lib/api/auth.service'
import { ApiClientError } from '@/lib/api/client'
import {
  formatPasswordChangeValidationErrors,
  mapPasswordChangeErrorMessage,
} from '@/lib/password-change-errors'
import { TwoFactorSection } from './TwoFactorSection'
import styles from './PerfilPage.module.css'

const MIN_PASSWORD_LENGTH = 8

export function SegurancaTab() {
  const { guard, token, isCollaborator } = useAuth()
  const { institutionId } = useInstitution()

  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [submitSuccess, setSubmitSuccess] = useState<string | null>(null)

  const handleSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      setSubmitError(null)
      setSubmitSuccess(null)

      const current = currentPassword.trim()
      const newPwd = newPassword.trim()
      const confirm = confirmPassword.trim()

      if (!current) {
        setSubmitError('Informe a senha atual.')
        return
      }
      if (!newPwd) {
        setSubmitError('Informe a nova senha.')
        return
      }
      if (newPwd.length < MIN_PASSWORD_LENGTH) {
        setSubmitError('A nova senha deve ter no mínimo 8 caracteres.')
        return
      }
      if (newPwd !== confirm) {
        setSubmitError('A confirmação da nova senha não confere.')
        return
      }
      if (newPwd === current) {
        setSubmitError('A nova senha deve ser diferente da senha atual.')
        return
      }

      if (!token || !guard) {
        setSubmitError('Sessão expirada. Faça login novamente.')
        return
      }

      if (guard !== 'admin' && guard !== 'institution') {
        setSubmitError('Alteração de senha não disponível para este tipo de conta.')
        return
      }

      setIsSubmitting(true)

      try {
        await changePassword(
          {
            current_password: current,
            new_password: newPwd,
            new_password_confirmation: confirm,
          },
          { guard, institutionId }
        )
        setSubmitSuccess('Senha atualizada com sucesso.')
        setCurrentPassword('')
        setNewPassword('')
        setConfirmPassword('')
      } catch (error) {
        if (error instanceof ApiClientError) {
          const message =
            error.errors && Object.keys(error.errors).length > 0
              ? formatPasswordChangeValidationErrors(error.errors)
              : mapPasswordChangeErrorMessage(error.message)
          setSubmitError(message)
        } else {
          setSubmitError('Não foi possível atualizar a senha. Tente novamente.')
        }
      } finally {
        setIsSubmitting(false)
      }
    },
    [currentPassword, newPassword, confirmPassword, token, guard, institutionId]
  )

  return (
    <div className={styles.card}>
      <div className={styles.sectionsWrapper}>
        <TwoFactorSection />
        <form className={styles.form} onSubmit={handleSubmit}>
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Alterar senha</h2>
            <p className={styles.sectionDescription}>
              {isCollaborator
                ? 'A senha alterada aqui é a da sua conta de colaborador, usada para entrar na plataforma.'
                : 'Mantenha sua conta segura atualizando sua senha regularmente.'}
            </p>
            <div className={styles.fields}>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="senha-atual">
                  Senha atual
                </label>
                <input
                  id="senha-atual"
                  type="password"
                  className={styles.input}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  disabled={isSubmitting}
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="senha-nova">
                  Nova senha
                </label>
                <input
                  id="senha-nova"
                  type="password"
                  className={styles.input}
                  placeholder="Mínimo 8 caracteres"
                  autoComplete="new-password"
                  minLength={MIN_PASSWORD_LENGTH}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  disabled={isSubmitting}
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="senha-confirmar">
                  Confirmar nova senha
                </label>
                <input
                  id="senha-confirmar"
                  type="password"
                  className={styles.input}
                  placeholder="Repita a nova senha"
                  autoComplete="new-password"
                  minLength={MIN_PASSWORD_LENGTH}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={isSubmitting}
                />
              </div>
            </div>
          </section>

          {submitError && (
            <div className={styles.errorBanner} role="alert">
              {submitError}
            </div>
          )}
          {submitSuccess && (
            <div className={styles.successBanner} role="status">
              {submitSuccess}
            </div>
          )}

          <button type="submit" className={styles.submit} disabled={isSubmitting}>
            {isSubmitting ? 'Atualizando...' : 'Atualizar senha'}
          </button>
        </form>
      </div>
    </div>
  )
}
